<template>
  <div>
    <div class="flex items-center justify-between">
      <h2 v-if="showTitle">{{ t('customExtras') }}</h2>
      <div class="ml-auto flex flex-wrap gap-2">
        <slot name="actions" />
        <CVButton
          :disabled="disabled || areAllExtrasReadOnly"
          @click.prevent="push(emptyCustomExtra())"
        >
          {{ t('addCustomExtra') }}
        </CVButton>
      </div>
    </div>

    <div v-if="fields.length" class="mb-4 mt-4 rounded-md bg-white pb-1 pl-4 pr-4 pt-1">
      <div
        v-for="(customBookedExtra, i) in fields"
        :key="i"
        class="relative pb-3 pr-8 pt-3"
        :class="{
          'border-b border-obsidian-200': i < fields.length - 1,
        }"
      >
        <div class="grid grid-cols-2 gap-4 md:grid-cols-5">
          <InputField
            class="col-span-2"
            :disabled="isRowDisabled(customBookedExtra.value.customVehicleExtraType)"
            :name="`customBookedExtras[${i}].description`"
            :label="t('description')"
          />
          <SelectField
            :class="{
              'col-span-2': !hasValue(customBookedExtra.value.customVehicleExtraType),
              'col-span-1': hasValue(customBookedExtra.value.customVehicleExtraType),
            }"
            :name="`customBookedExtras[${i}].customVehicleExtraType`"
            :label="t('customVehicleExtraType')"
            :options="getExtraTypeOptionsFor(customBookedExtra.value.customVehicleExtraType)"
            empty-value-to="null"
            :disabled="
              isRowDisabled(customBookedExtra.value.customVehicleExtraType) ||
              isExtraTypeExcluded(customBookedExtra.value.customVehicleExtraType)
            "
          />
          <InputField
            v-if="hasValue(customBookedExtra.value.customVehicleExtraType)"
            :disabled="isRowDisabled(customBookedExtra.value.customVehicleExtraType)"
            class="col-span-1"
            :name="`customBookedExtras[${i}].value`"
            :label="getValueLabel(customBookedExtra.value.customVehicleExtraType)"
            type="number"
            empty-value-to="undefined"
          />
          <InputField
            :disabled="isRowDisabled(customBookedExtra.value.customVehicleExtraType)"
            :name="`customBookedExtras[${i}].price`"
            :label="t('price')"
            adornment="€"
            type="number"
            :max-fraction-digits="2"
            class="max-md:col-span-2"
          />
        </div>

        <div
          v-if="
            customBookedExtra.value.photoFileUrls && customBookedExtra.value.photoFileUrls.length
          "
          class="mt-2 flex flex-wrap gap-2"
        >
          <button
            v-for="(url, idx) in customBookedExtra.value.photoFileUrls"
            :key="idx"
            type="button"
            class="cursor-pointer"
            @click="openPhotoPreview(url, customBookedExtra.value.photoFileKeys?.[idx])"
          >
            <img
              :src="url"
              crossorigin="anonymous"
              :alt="t('photo')"
              class="h-12 w-12 rounded-md object-cover"
            />
          </button>
        </div>

        <button
          v-if="
            !areAllExtrasReadOnly &&
            !isExtraTypeFrozen(customBookedExtra.value.customVehicleExtraType)
          "
          :disabled="disabled"
          class="absolute right-0 top-[2.8rem] cursor-pointer p-2 text-lg leading-3 text-red-600"
          @click.prevent="removeField(i)"
        >
          ×
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFieldArray } from 'vee-validate';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { CustomBookedExtra } from '@/entities/bookings/booked-extra';
import { VehicleExtraType } from '@carvia/ros-client-types';
import { areAllExtrasFrozen, useIsExtraTypeFrozen } from './use-is-extra-type-frozen';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { fetchBlobFromUrl } from '@/utils/download-file';

const props = withDefaults(
  defineProps<{
    booking: Booking;
    disabled?: boolean;
    showTitle?: boolean;
    excludeExtraTypes?: VehicleExtraType[];
  }>(),
  {
    showTitle: true,
    excludeExtraTypes: () => [],
  },
);

const { t } = useI18n();
const pdfPreview = usePdfPreview();
const { push, replace, fields } = useFieldArray<CustomBookedExtra>('customBookedExtras');

const areAllExtrasReadOnly = computed(() => areAllExtrasFrozen(props.booking.status));
const isExtraTypeFrozen = useIsExtraTypeFrozen(props.booking.status);

const isRowDisabled = (extraType: VehicleExtraType | null) =>
  !!props.disabled || areAllExtrasReadOnly.value || isExtraTypeFrozen(extraType);

const isExtraTypeExcluded = (extraType: VehicleExtraType | null) =>
  extraType != null && props.excludeExtraTypes.includes(extraType);

const extraTypeOptions = computed(() =>
  [null, ...Object.values(VehicleExtraType)].map((type) => ({
    label: type
      ? isReplacementCustomExtra(type)
        ? `${t('replace', { extraType: t(`vehicleExtras.${type}`) })}`
        : t(`vehicleExtras.${type}`)
      : t('custom'),
    value: type,
  })),
);

const getExtraTypeOptionsFor = (currentType: VehicleExtraType | null) =>
  extraTypeOptions.value.filter(
    ({ value }) =>
      value === currentType ||
      ((value == null || !props.excludeExtraTypes.includes(value)) && !isExtraTypeFrozen(value)),
  );

const isReplacementCustomExtra = (extraType: VehicleExtraType | null) =>
  extraType !== VehicleExtraType.TRANSFER_RETURN &&
  extraType !== VehicleExtraType.TRANSFER_PICKUP &&
  extraType !== VehicleExtraType.CLEANING_FEE;

const hasValue = (extraType: VehicleExtraType | null) =>
  extraType === VehicleExtraType.ADDITIONAL_DRIVER ||
  extraType === VehicleExtraType.YOUNG_DRIVER ||
  extraType === VehicleExtraType.DEDUCTIBLE ||
  extraType === VehicleExtraType.EXTRA_KM ||
  extraType === VehicleExtraType.TRANSFER_PICKUP ||
  extraType === VehicleExtraType.TRANSFER_RETURN ||
  extraType === VehicleExtraType.BABY_SEAT ||
  extraType === VehicleExtraType.CHILD_SEAT_0 ||
  extraType === VehicleExtraType.CHILD_SEAT_1 ||
  extraType === VehicleExtraType.BOOSTER_SEAT;

const getValueLabel = (extraType: VehicleExtraType | null) => {
  switch (extraType) {
    case VehicleExtraType.ADDITIONAL_DRIVER:
      return t('additionalDrivers');
    case VehicleExtraType.YOUNG_DRIVER:
      return t('number');
    case VehicleExtraType.DEDUCTIBLE:
      return t('deductible');
    case VehicleExtraType.EXTRA_KM:
    case VehicleExtraType.TRANSFER_PICKUP:
    case VehicleExtraType.TRANSFER_RETURN:
      return t('kilometers');
    case VehicleExtraType.BABY_SEAT:
      return t('babySeatCount');
    case VehicleExtraType.CHILD_SEAT_0:
      return t('childSeat0Count');
    case VehicleExtraType.CHILD_SEAT_1:
      return t('childSeat1Count');
    case VehicleExtraType.BOOSTER_SEAT:
      return t('boosterSeatCount');
    default:
      return t('value');
  }
};

const emptyCustomExtra = (): CustomBookedExtra => ({
  description: '',
  price: 0,
  customVehicleExtraType: null,
  value: undefined,
  photoFileKeys: [],
});

const removeField = (index: number) => {
  replace(fields.value.filter((_, i) => i !== index).map((field) => field.value));
};

const openPhotoPreview = async (url: string, fileKey?: string) => {
  await pdfPreview.openPdfPreview({
    title: t('photo'),
    fileName: fileKey,
    load: () => fetchBlobFromUrl(url),
    errorMessage: t('couldNotLoadPhoto'),
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "customExtras": "Custom Extras",
    "addCustomExtra": "+ Add Custom Extra",
    "description": "Description",
    "price": "Price",
    "custom": "Custom Extra",
    "customVehicleExtraType": "Extra Type",
    "replace": "Replace {extraType}",
    "additionalDrivers": "Add. drivers",
    "number": "Number",
    "deductible": "Deductible",
    "kilometers": "Kilometers",
    "value": "Value",
    "photo": "Photo",
    "couldNotLoadPhoto": "The file could not be loaded. Please try again."
  },
  "de": {
    "customExtras": "Individuelle Extras",
    "addCustomExtra": "+ Extra hinzufügen",
    "description": "Beschreibung",
    "price": "Preis",
    "custom": "Individuelles Extra",
    "customVehicleExtraType": "Extratyp",
    "replace": "{extraType} ersetzen",
    "additionalDrivers": "Zusatzfahrer",
    "number": "Anzahl",
    "deductible": "SB.",
    "kilometers": "Kilometer",
    "value": "Wert",
    "photo": "Foto",
    "couldNotLoadPhoto": "Die Datei konnte nicht geladen werden. Bitte versuche es erneut."
  },
  "es": {
    "customExtras": "Extras personalizados",
    "addCustomExtra": "+ Añadir extra personalizado",
    "description": "Descripción",
    "price": "Precio",
    "custom": "Extra personalizado",
    "customVehicleExtraType": "Tipo de extra",
    "replace": "Sustituir {extraType}",
    "additionalDrivers": "Cond. adicionales",
    "number": "Cantidad",
    "deductible": "Franquicia",
    "kilometers": "Kilómetros",
    "value": "Valor",
    "photo": "Foto",
    "couldNotLoadPhoto": "No se pudo cargar el archivo. Inténtelo de nuevo."
  }
}
</i18n>
